import {
  CONTINUATION,
  type Continuation,
  UNEMPLOYMENT,
  UNEMPLOYMENT_LEVELS,
  type UnemploymentRule,
} from './coverage'
import { BASE, D, DEF, type Discrepancy, type Rated, S } from './data'

export { BASE, D }

export type AtlasState = {
  c: string
  n: string
  sev: Rated
  warn: Rated
  fire: Rated
  quit: string
  pto: Rated
  sep: string | null
  other: string[]
  disc: string[]
  /** State health-coverage continuation (mini-COBRA). */
  mc: Continuation
  /** How severance affects unemployment benefits. */
  ui: UnemploymentRule
}

export const STATES: AtlasState[] = S.map(([c, n, d]) => ({
  c,
  n,
  sev: d.sev ?? DEF.sev,
  warn: d.warn ?? DEF.warn,
  fire: d.fire,
  quit: d.quit,
  pto: d.pto ?? DEF.pto,
  sep: d.sep ?? null,
  other: d.other ?? [],
  disc: [...(d.disc ?? [])],
  mc: CONTINUATION[c] ?? [0, '-', 'None', '-', '', 's'],
  ui: UNEMPLOYMENT[c] ?? [1, 'Unknown', 's'],
}))

export const STATE_BY_CODE: Record<string, AtlasState> = Object.fromEntries(
  STATES.map((s) => [s.c, s]),
)

// Link every discrepancy to the states it names.
for (const d of D) {
  for (const code of d.states) {
    const st = STATE_BY_CODE[code]
    if (st && !st.disc.includes(d.id)) st.disc.push(d.id)
  }
}

export const DISC_BY_ID: Record<string, Discrepancy> = Object.fromEntries(D.map((d) => [d.id, d]))

/** Tile-map layout: [column, row]. */
export const TILE_POS: Record<string, [number, number]> = {
  AK: [0, 0],
  ME: [11, 0],
  VT: [10, 1],
  NH: [11, 1],
  WA: [1, 2],
  ID: [2, 2],
  MT: [3, 2],
  ND: [4, 2],
  MN: [5, 2],
  IL: [6, 2],
  WI: [7, 2],
  MI: [8, 2],
  NY: [9, 2],
  RI: [10, 2],
  MA: [11, 2],
  OR: [1, 3],
  NV: [2, 3],
  WY: [3, 3],
  SD: [4, 3],
  IA: [5, 3],
  IN: [6, 3],
  OH: [7, 3],
  PA: [8, 3],
  NJ: [9, 3],
  CT: [10, 3],
  CA: [1, 4],
  UT: [2, 4],
  CO: [3, 4],
  NE: [4, 4],
  MO: [5, 4],
  KY: [6, 4],
  WV: [7, 4],
  VA: [8, 4],
  MD: [9, 4],
  DE: [10, 4],
  AZ: [2, 5],
  NM: [3, 5],
  KS: [4, 5],
  AR: [5, 5],
  TN: [6, 5],
  NC: [7, 5],
  SC: [8, 5],
  OK: [4, 6],
  LA: [5, 6],
  MS: [6, 6],
  AL: [7, 6],
  GA: [8, 6],
  HI: [0, 7],
  TX: [4, 7],
  FL: [9, 7],
}

export type LensKey = 'sev' | 'warn' | 'fire' | 'pto' | 'sep' | 'mc' | 'ui' | 'disc'

export type Lens = {
  label: string
  get: (s: AtlasState) => number
  levels: Record<number, string>
  note: string
}

export const LENSES: Record<LensKey, Lens> = {
  sev: {
    label: 'Severance obligation',
    get: (s) => s.sev[0],
    levels: {
      3: 'Mandatory in mass layoffs',
      2: 'Mandatory in closings only',
      1: 'Related benefit mandate (health continuation)',
      0: 'Contractual only',
    },
    note: 'Only New Jersey requires cash severance in mass layoffs generally. Maine and Hawaii require payments only when a covered facility closes or relocates.',
  },
  warn: {
    label: 'Mini-WARN',
    get: (s) => s.warn[0],
    levels: { 2: 'Mandatory state law', 1: 'Voluntary / limited', 0: 'Federal WARN only' },
    note: 'State laws usually cover smaller employers (as few as 25 in Iowa) or require longer notice (90 days in NY, NJ, ME, NE).',
  },
  fire: {
    label: 'Final pay (fired)',
    get: (s) => s.fire[0],
    levels: {
      3: 'Same day, 24h or next business day',
      2: 'Within 3 to 7 days',
      1: 'Next payday or later',
      0: 'No state statute',
    },
    note: 'Deadlines for involuntary terminations. Resignation deadlines are usually later and appear in each state profile.',
  },
  pto: {
    label: 'PTO payout',
    get: (s) => s.pto[0],
    levels: {
      3: 'Required',
      2: 'Required unless written policy says otherwise',
      0: 'Employer policy governs',
    },
    note: 'In "Required" states, earned vacation counts as wages and generally can’t be forfeited at separation.',
  },
  sep: {
    label: 'Separation notice',
    get: (s) => (s.sep ? 2 : 0),
    levels: { 2: 'State-mandated notice / form', 0: 'None required' },
    note: 'Mostly unemployment-insurance notices that must be handed over at separation.',
  },
  mc: {
    label: 'State continuation',
    get: (s) => s.mc[0],
    levels: {
      3: '18+ months or extends federal COBRA',
      2: '9 to 12 months',
      1: 'Under 9 months or limited',
      0: 'No general law',
    },
    note: 'State "mini-COBRA" laws, mostly for insured plans of employers too small for federal COBRA. See the health coverage section for details.',
  },
  ui: {
    label: 'Severance vs. unemployment',
    get: (s) => s.ui[0],
    levels: UNEMPLOYMENT_LEVELS,
    note: 'How the state unemployment agency treats severance. Lump-sum vs. installment payment often matters.',
  },
  disc: {
    label: 'Source disagreement',
    get: (s) => (s.disc.length ? 2 : 0),
    levels: { 2: 'Sources disagree', 0: 'No disagreement found' },
    note: 'States where at least one source article conflicts with another source or with the statute. Details are in the discrepancy section.',
  },
}

const pick = (f: (s: AtlasState) => unknown) => STATES.filter((s) => f(s)).map((s) => s.c)

export type Exception = {
  h: string
  p: string
  /** Rows of [state code, explanation]. */
  list?: [string, string][]
  /** Chip groups of state codes, with an optional lead-in label. */
  chips?: { label?: string; codes: string[] }[]
  count?: number
}

export const EXCEPTIONS: Exception[] = [
  {
    h: 'Severance is mandatory in specific cases',
    p: 'The only real exceptions to the "severance is voluntary" rule. All three apply only to layoffs or closings at larger employers.',
    list: [
      [
        'NJ',
        'Mass layoffs: 1 week per year of service, uncapped, +4 weeks if notice is short. 100+ employees nationwide, 50+ terminated in NJ.',
      ],
      [
        'ME',
        'Closings or mass layoffs at 100+ employee establishments: 1 week per year for employees with 3+ years.',
      ],
      [
        'HI',
        'Closings or relocations: up to 4 weeks of wage-minus-UI "dislocated worker allowance."',
      ],
    ],
  },
  {
    h: 'Employer-paid health continuation',
    p: 'No cash payment, but the employer must keep paying for coverage.',
    list: [
      [
        'CT',
        'Up to 120 days of fully employer-paid group health coverage on relocation or closure.',
      ],
      ['MA', 'Continued group health coverage in qualifying plant closings.'],
    ],
  },
  {
    h: 'Mandatory mini-WARN laws',
    p: 'Lower headcounts, longer notice, or triggers the federal law misses.',
    count: pick((s) => s.warn[0] === 2).length,
    list: [
      ['IA', 'Lowest threshold: 25 employees, 30 days.'],
      ['NY', '50 employees, 90 days.'],
      ['NJ', '100 employees, 90 days, plus severance.'],
      ['ME', '90 days for closings.'],
      ['NE', 'New in 2026: 90 days.'],
      ['CA', '75 employees, any 50-person layoff.'],
      ['IL', '75 employees, 60 days.'],
      ['WA', '50 employees, 60 days (2025).'],
      ['WI', '50 employees, 60 days.'],
      ['MD', '50 employees, 60 days; enforced from Oct 2025.'],
      ['VT', '50 employees; 45 days to state, 30 to workers.'],
      ['HI', '50 employees, 60 days.'],
      ['DE', '100 employees, 60 days.'],
      ['NH', '100 employees, 60 days.'],
      ['OH', 'Mirrors federal; 2025.'],
      ['TN', 'Only 50 to 99 employee firms; no set period.'],
    ],
  },
  {
    h: 'Voluntary or limited layoff-notice laws',
    p: 'Encouraged notice, state-only reporting, or narrow triggers.',
    count: pick((s) => s.warn[0] === 1).length,
    chips: [{ codes: pick((s) => s.warn[0] === 1) }],
  },
  {
    h: 'Release and agreement rules beyond OWBPA',
    p: 'State rules that change how a severance agreement must be drafted or timed.',
    list: [
      [
        'CA',
        'Carve-out language, attorney notice, at least 5 business days; waiver of Civ. Code §1542.',
      ],
      [
        'IL',
        '21 days to consider + 7 to revoke when unlawful-practice allegations are kept confidential.',
      ],
      ['MN', '15-day rescission for releases of MHRA claims.'],
      [
        'NY',
        '21/7 days for discrimination-settlement NDAs. Pending bill would extend 21/7 to all severance.',
      ],
      [
        'NJ',
        'NDAs hiding discrimination or harassment details unenforceable against the employee.',
      ],
    ],
  },
  {
    h: 'Same-day to next-business-day final pay when fired',
    p: 'Paying on the next regular payday isn’t enough in these states. Waiting-time penalties can be large (up to 30 days’ wages in CA, 60 in MO, treble damages in MA).',
    count: pick((s) => s.fire[0] === 3).length,
    chips: [{ codes: pick((s) => s.fire[0] === 3) }],
  },
  {
    h: 'PTO must be paid out',
    p: 'Required regardless of policy (RI after 1 year; ME at 11+ employees). Another group requires payout unless a written policy says otherwise.',
    chips: [
      { codes: pick((s) => s.pto[0] === 3) },
      { label: 'Unless a written policy says otherwise:', codes: pick((s) => s.pto[0] === 2) },
    ],
  },
  {
    h: 'State separation notices',
    p: 'Mostly UI-availability forms that must be handed over at separation. Tennessee (24 hours) and Louisiana (3 days) are the tightest.',
    count: pick((s) => s.sep).length,
    chips: [{ codes: pick((s) => s.sep) }],
  },
  {
    h: 'Noncompete limits that shape severance terms',
    p: 'Affects whether a non-compete in a severance package is enforceable.',
    list: [
      ['CA', 'Void, including out-of-state agreements.'],
      ['MN', 'Banned since Jul 2023.'],
      ['ND', 'Void.'],
      ['OK', 'Generally void.'],
      ['WY', 'Largely void since Jul 2025.'],
      ['WA', 'Salary floor now; near-total ban from 30 Jun 2027.'],
      ['CO', 'Salary floor $130,014 (2026).'],
      ['IL', 'Void at $75,000 or less.'],
      ['MA', '12-month cap + garden leave.'],
    ],
  },
  {
    h: 'Not at-will, and service letters',
    p: 'Montana needs good cause after probation. Six states make employers give a reason for termination on request.',
    list: [
      ['MT', 'Wrongful Discharge from Employment Act. Also requires a service letter.'],
      ['MO', 'Service letter (RSMo §290.140).'],
      ['KS', 'Service letter: tenure, job classification and wage rate.'],
      ['ME', 'Reason for discharge on request.'],
      ['MN', 'Reason for termination on written request.'],
      ['IN', 'Letter stating whether the employee quit or was discharged.'],
    ],
  },
]

const count = (f: (s: AtlasState) => unknown) => STATES.filter((s) => f(s)).length

export type Stat = { n: number; label: string; target: string; lens?: LensKey }

export const STATS: Stat[] = [
  {
    n: count((s) => s.sev[0] >= 2),
    label: 'states require severance-type payments',
    target: 'exceptions',
  },
  {
    n: count((s) => s.warn[0] === 2),
    label: 'mandatory state mini-WARN laws',
    target: 'explorer',
    lens: 'warn',
  },
  {
    n: count((s) => s.fire[0] === 3),
    label: 'states: fired workers paid same day to next business day',
    target: 'explorer',
    lens: 'fire',
  },
  {
    n: count((s) => s.pto[0] === 3),
    label: 'states require PTO payout outright',
    target: 'explorer',
    lens: 'pto',
  },
  {
    n: count((s) => s.sep),
    label: 'states require a separation notice',
    target: 'explorer',
    lens: 'sep',
  },
  { n: D.length, label: 'discrepancies found across sources', target: 'discrepancies' },
]

export const countWhere = (f: (s: AtlasState) => boolean) => STATES.filter(f).length

export const SOURCE_ARTICLES: [string, string, string][] = [
  [
    'Workforce.com — Severance pay laws by state',
    'https://www.workforce.com/news/severance-pay-laws-by-state',
    '26 Mar 2026',
  ],
  [
    'SeveranceCalc — State severance laws',
    'https://severancecalc.com/guides/state-severance-laws',
    'updated 6 Sep 2026',
  ],
  [
    'Foothold America — US severance pay laws',
    'https://www.footholdamerica.com/blog/us-severance-pay-laws-state-requirements/',
    'updated 7 Aug 2026',
  ],
  [
    'SixFifty — Termination requirements by state',
    'https://www.sixfifty.com/resource-library/employee-termination-requirements-by-state/',
    'interactive map only; not used for claims',
  ],
]
